import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  #controller = null;
  #revision = 0;
  #animationRevision = 0;

  get currentCartCount() {
    const count = Number.parseInt(
      this.refs.cartBubbleCount.textContent || '0',
      10
    );

    return Number.isFinite(count) ? count : 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent =
      value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    document.addEventListener(
      'theme-drawer:open',
      this.onDrawerOpen
    );

    window.addEventListener('pageshow', this.onPageShow);
    window.addEventListener('focus', this.onWindowFocus);

    // Old cached counts must never override the real cart.
    try {
      sessionStorage.removeItem('cart-count');
    } catch {
      // Storage may be disabled.
    }

    void this.syncCartCount();
  }

  disconnectedCallback() {
    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    document.removeEventListener(
      'theme-drawer:open',
      this.onDrawerOpen
    );

    window.removeEventListener('pageshow', this.onPageShow);
    window.removeEventListener('focus', this.onWindowFocus);

    this.#revision += 1;
    this.#animationRevision += 1;
    this.#controller?.abort();

    super.disconnectedCallback();
  }

  onPageShow = () => {
    void this.syncCartCount();
  };

  onWindowFocus = () => {
    void this.syncCartCount();
  };

  onDrawerOpen = (event) => {
    if (event.target?.id === 'cart-drawer') {
      void this.syncCartCount();
    }
  };

  onCartUpdate = (event) => {
    // Ignore any count request started before this cart update.
    const revision = ++this.#revision;
    this.#controller?.abort();

    Promise.resolve(event.promise)
      .then(() => {
        if (!this.isConnected || revision !== this.#revision) {
          return;
        }

        // Read the actual cart after the update has finished.
        return this.syncCartCount(true);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-icon] Cart update failed:', error);
        }

        if (this.isConnected && revision === this.#revision) {
          void this.syncCartCount();
        }
      });
  };

  syncCartCount = async (animate = false) => {
    if (!this.isConnected) return;

    const revision = ++this.#revision;

    this.#controller?.abort();

    const controller = new AbortController();
    this.#controller = controller;

    const root = window.Shopify?.routes?.root || '/';
    const cartUrl = `${root.replace(/\/?$/, '/')}cart.js`;

    try {
      const response = await fetch(cartUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Cart request failed: ${response.status}`);
      }

      const cart = await response.json();
      const count = cart.item_count;

      if (
        !this.isConnected ||
        revision !== this.#revision ||
        !Number.isInteger(count) ||
        count < 0
      ) {
        return;
      }

      await this.renderCartBubble(count, animate);
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[cart-icon] Count sync failed:', error);
      }

      // A failed request must not falsely mark the cart as empty.
    } finally {
      if (this.#controller === controller) {
        this.#controller = null;
      }
    }
  };

  renderCartBubble = async (itemCount, animate = true) => {
    const animationRevision = ++this.#animationRevision;
    const { cartBubble, cartBubbleText, cartBubbleCount } = this.refs;
    const isEmpty = itemCount === 0;
    const previousCount = this.currentCartCount;

    cartBubble.classList.remove('cart-bubble--animating');

    cartBubbleCount.classList.toggle('hidden', isEmpty);
    cartBubble.classList.toggle('visually-hidden', isEmpty);
    cartBubble.toggleAttribute('data-maintain-ratio', itemCount <= 99);

    this.currentCartCount = itemCount;

    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      !isEmpty
    );

    if (!animate || isEmpty || previousCount === itemCount) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    if (
      !this.isConnected ||
      animationRevision !== this.#animationRevision
    ) {
      return;
    }

    cartBubble.classList.add('cart-bubble--animating');

    try {
      await onAnimationEnd(cartBubbleText);
    } finally {
      if (animationRevision === this.#animationRevision) {
        cartBubble.classList.remove('cart-bubble--animating');
      }
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}